import React, { useState } from "react";
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { Separator } from "@/components/ui/separator";
import { Card } from "@/components/ui/card";
import { FileText, BookOpen, BookCopy, FileDown, CheckCircle2, Users, Play, ChevronLeft, ChevronRight } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";

// Custom components for the lecture
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="relative bg-gradient-to-r from-indigo-50 via-blue-50 to-slate-50 border border-indigo-200 rounded-lg p-6 my-6 overflow-hidden">
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-indigo-500 to-blue-600"></div>
      <div className="flex items-start">
        <div className="flex-shrink-0 mt-1">
          <div className="w-7 h-7 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-full flex items-center justify-center">
            <CheckCircle2 className="h-3.5 w-3.5 text-white" />
          </div>
        </div>
        <div className="ml-3">
          <h4 className="text-base font-bold text-slate-900 mb-2 tracking-tight">Key Takeaway</h4>
          <p className="text-slate-700 leading-relaxed text-sm">{children}</p>
        </div>
      </div>
    </div>
  );
};

const ScientificDiagram = ({ 
  title, 
  caption, 
  children 
}: { 
  title: string; 
  caption: string; 
  children: React.ReactNode;
}) => {
  return (
    <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-lg shadow-sm p-5 mb-6 overflow-hidden">
      <div className="mb-4">
        <h3 className="text-lg font-bold text-slate-900 mb-2 tracking-tight">{title}</h3>
        <p className="text-xs text-slate-600 leading-relaxed">{caption}</p>
      </div>
      <div className="bg-white rounded-lg border border-slate-100 p-3">
        {children}
      </div>
    </div>
  );
};

const VideoPlaceholder = () => {
  return (
    <div className="bg-slate-100 rounded-lg border border-slate-200 p-12 flex flex-col items-center justify-center mb-6">
      <Play className="h-12 w-12 text-slate-400 mb-4" />
      <p className="text-slate-500 text-sm">Video content will be available soon</p>
    </div>
  );
};

// Slideshow component
interface Slide {
  id: number;
  title: string;
  content: React.ReactNode;
}

const Slideshow = ({ slides }: { slides: Slide[] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const goToNextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? prev : prev + 1));
  };
  
  const goToPrevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? prev : prev - 1));
  };
  
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden mb-8">
      {/* Slide navigation */}
      <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <h3 className="text-sm font-medium text-slate-700">
          Slide {currentSlide + 1} of {slides.length}: {slides[currentSlide].title}
        </h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={goToPrevSlide}
            disabled={currentSlide === 0}
            className={`p-1 rounded-full ${
              currentSlide === 0
                ? "text-slate-400 cursor-not-allowed"
                : "text-slate-700 hover:bg-slate-200"
            }`}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={goToNextSlide}
            disabled={currentSlide === slides.length - 1}
            className={`p-1 rounded-full ${
              currentSlide === slides.length - 1
                ? "text-slate-400 cursor-not-allowed"
                : "text-slate-700 hover:bg-slate-200"
            }`}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
      
      {/* Slide content */}
      <div className="p-6">
        <div className="mb-4">
          <h2 className="text-xl font-bold text-slate-900 mb-2">{slides[currentSlide].title}</h2>
          <Separator className="bg-slate-200" />
        </div>
        <div>{slides[currentSlide].content}</div>
      </div>
    </div>
  );
};

const LifestyleFoundations3: React.FC = () => {
  // Define slides for the presentation
  const slides: Slide[] = [
    // Slide 1: Title Slide
    {
      id: 1,
      title: "Lifestyle Interventions for Longevity and Health Optimization: A SHIFT Framework Approach",
      content: (
        <div className="space-y-4">
          <p className="text-slate-600 text-center mb-6">Practical Applications for Clinical Practice</p>
          <div className="flex justify-center">
            <svg width="500" height="300" viewBox="0 0 500 300" xmlns="http://www.w3.org/2000/svg">
              {/* Background gradient */}
              <defs>
                <linearGradient id="titleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#f0f4ff" />
                  <stop offset="100%" stopColor="#e0e7ff" />
                </linearGradient>
              </defs>
              
              {/* Background */}
              <rect x="0" y="0" width="500" height="300" rx="10" fill="url(#titleGradient)" />
              
              {/* Decorative elements */}
              <circle cx="100" cy="50" r="30" fill="#c7d2fe" opacity="0.6" />
              <circle cx="400" cy="250" r="40" fill="#c7d2fe" opacity="0.6" />
              <circle cx="450" cy="80" r="20" fill="#c7d2fe" opacity="0.6" />
              <circle cx="50" cy="200" r="25" fill="#c7d2fe" opacity="0.6" />
              
              {/* Title */}
              <text x="250" y="100" textAnchor="middle" className="text-xl font-bold" fill="#1e293b">
                Lifestyle Interventions for Longevity
              </text>
              <text x="250" y="130" textAnchor="middle" className="text-xl font-bold" fill="#1e293b">
                and Health Optimization
              </text>
              
              {/* Subtitle */}
              <text x="250" y="170" textAnchor="middle" className="text-base" fill="#475569">
                A SHIFT Framework Approach
              </text>
              
              {/* Bottom text */}
              <text x="250" y="220" textAnchor="middle" className="text-sm" fill="#64748b">
                Practical Applications for Clinical Practice
              </text>
            </svg>
          </div>
        </div>
      )
    },
    
    // Slide 2: The Paradigm Shift in Modern Medicine
    {
      id: 2,
      title: "Beyond Disease Treatment: The Rise of Longevity Medicine",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <ul className="space-y-3">
              <li className="flex items-start">
                <div className="h-5 w-5 flex-shrink-0 rounded-full bg-indigo-100 flex items-center justify-center mt-0.5 mr-3">
                  <div className="h-2 w-2 rounded-full bg-indigo-600"></div>
                </div>
                <div>
                  <span className="font-medium text-slate-900">Traditional medicine:</span> Primarily reactive, focused on treating established diseases.
                </div>
              </li>
              <li className="flex items-start">
                <div className="h-5 w-5 flex-shrink-0 rounded-full bg-indigo-100 flex items-center justify-center mt-0.5 mr-3">
                  <div className="h-2 w-2 rounded-full bg-indigo-600"></div>
                </div>
                <div>
                  <span className="font-medium text-slate-900">Longevity Medicine:</span> Proactive, aiming to extend <span className="font-medium">healthspan</span> (years of healthy life) and optimize overall longevity.
                </div>
              </li>
              <li className="flex items-start">
                <div className="h-5 w-5 flex-shrink-0 rounded-full bg-indigo-100 flex items-center justify-center mt-0.5 mr-3">
                  <div className="h-2 w-2 rounded-full bg-indigo-600"></div>
                </div>
                <div>
                  <span className="font-medium text-slate-900">Addressing global burden:</span> Cardiovascular disease, type 2 diabetes, neurodegenerative disorders, cancer.
                </div>
              </li>
              <li className="flex items-start">
                <div className="h-5 w-5 flex-shrink-0 rounded-full bg-indigo-100 flex items-center justify-center mt-0.5 mr-3">
                  <div className="h-2 w-2 rounded-full bg-indigo-600"></div>
                </div>
                <div>
                  <span className="font-medium text-slate-900">Goal:</span> Enhance vitality and functional capacity throughout life, not just increase lifespan.
                </div>
              </li>
            </ul>
          </div>
          
          <ScientificDiagram 
            title="The Paradigm Shift in Medicine" 
            caption="Evolution from reactive disease treatment to proactive healthspan optimization"
          >
            <svg width="450" height="200" viewBox="0 0 450 200" xmlns="http://www.w3.org/2000/svg">
              {/* Arrow background */}
              <rect x="50" y="80" width="350" height="60" rx="30" fill="#f1f5f9" />
              
              {/* Gradient arrow */}
              <defs>
                <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#e0e7ff" />
                  <stop offset="100%" stopColor="#c7d2fe" />
                </linearGradient>
              </defs>
              
              {/* Arrow body */}
              <rect x="50" y="80" width="350" height="60" rx="30" fill="url(#arrowGradient)" />
              
              {/* Arrow head */}
              <polygon points="380,110 365,90 365,130" fill="#818cf8" />
              
              {/* Left side: Reactive */}
              <text x="100" y="65" textAnchor="middle" fill="#334155" fontWeight="bold" fontSize="14">Reactive</text>
              <text x="100" y="110" textAnchor="middle" fill="#1e293b" fontSize="12">(Disease Treatment)</text>
              
              {/* Right side: Proactive */}
              <text x="350" y="65" textAnchor="middle" fill="#334155" fontWeight="bold" fontSize="14">Proactive</text>
              <text x="350" y="110" textAnchor="middle" fill="#1e293b" fontSize="12">(Healthspan Optimization)</text>
              
              {/* Disease icons on left */}
              <circle cx="60" cy="160" r="15" fill="#fecaca" />
              <text x="60" y="164" textAnchor="middle" fill="#b91c1c" fontSize="12">CVD</text>
              
              <circle cx="100" cy="160" r="15" fill="#fed7aa" />
              <text x="100" y="164" textAnchor="middle" fill="#c2410c" fontSize="12">T2D</text>
              
              <circle cx="140" cy="160" r="15" fill="#bfdbfe" />
              <text x="140" y="164" textAnchor="middle" fill="#1d4ed8" fontSize="12">ND</text>
              
              <circle cx="180" cy="160" r="15" fill="#fecdd3" />
              <text x="180" y="164" textAnchor="middle" fill="#be185d" fontSize="12">CA</text>
            </svg>
          </ScientificDiagram>
        </div>
      )
    },
    
    // Slide 3: Core Pillars of Lifestyle Intervention
    {
      id: 3,
      title: "Foundational Lifestyle Interventions: The Cornerstones of Healthspan",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <p className="text-slate-700">
              Nutrition, Exercise/Movement, and Sleep are primary determinants of:
            </p>
            <ul className="space-y-3 pl-6">
              <li className="flex items-start">
                <div className="h-5 w-5 flex-shrink-0 rounded-full bg-indigo-100 flex items-center justify-center mt-0.5 mr-3">
                  <div className="h-2 w-2 rounded-full bg-indigo-600"></div>
                </div>
                <span>Cellular function</span>
              </li>
              <li className="flex items-start">
                <div className="h-5 w-5 flex-shrink-0 rounded-full bg-indigo-100 flex items-center justify-center mt-0.5 mr-3">
                  <div className="h-2 w-2 rounded-full bg-indigo-600"></div>
                </div>
                <span>Metabolic regulation</span>
              </li>
              <li className="flex items-start">
                <div className="h-5 w-5 flex-shrink-0 rounded-full bg-indigo-100 flex items-center justify-center mt-0.5 mr-3">
                  <div className="h-2 w-2 rounded-full bg-indigo-600"></div>
                </div>
                <span>Hormonal balance</span>
              </li>
              <li className="flex items-start">
                <div className="h-5 w-5 flex-shrink-0 rounded-full bg-indigo-100 flex items-center justify-center mt-0.5 mr-3">
                  <div className="h-2 w-2 rounded-full bg-indigo-600"></div>
                </div>
                <span>Resilience against age-related decline</span>
              </li>
            </ul>
            <p className="text-slate-700 mt-4">
              These habits are interconnected and profoundly impact health trajectory.
            </p>
          </div>
          
          <ScientificDiagram 
            title="Core Pillars of Lifestyle Intervention" 
            caption="The three interconnected foundations of healthspan optimization"
          >
            <svg width="450" height="300" viewBox="0 0 450 300" xmlns="http://www.w3.org/2000/svg">
              {/* Venn diagram circles */}
              <circle cx="175" cy="150" r="100" fill="#e0e7ff" fillOpacity="0.7" stroke="#818cf8" strokeWidth="2" />
              <circle cx="275" cy="150" r="100" fill="#dbeafe" fillOpacity="0.7" stroke="#60a5fa" strokeWidth="2" />
              <circle cx="225" cy="230" r="100" fill="#dcfce7" fillOpacity="0.7" stroke="#4ade80" strokeWidth="2" />
              
              {/* Circle labels */}
              <text x="175" y="150" textAnchor="middle" fill="#4338ca" fontWeight="bold" fontSize="14">Nutrition</text>
              <text x="275" y="150" textAnchor="middle" fill="#1d4ed8" fontWeight="bold" fontSize="14">Sleep</text>
              <text x="225" y="230" textAnchor="middle" fill="#16a34a" fontWeight="bold" fontSize="14">Exercise/Movement</text>
              
              {/* Center intersection */}
              <text x="225" y="180" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="12">Healthspan</text>
              <text x="225" y="195" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="12">&</text>
              <text x="225" y="210" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="12">Longevity</text>
            </svg>
          </ScientificDiagram>
        </div>
      )
    },
    
    // Slide 4: Introducing the SHIFT Framework
    {
      id: 4,
      title: "The SHIFT Framework: Understanding Lifestyle's Impact",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700">
            A scaffold to examine how nutrition, exercise, and sleep modulate aging and healthspan.
          </p>
          
          <div className="space-y-3 pl-6">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 text-indigo-600 font-bold text-xl">S</div>
              <div>
                <span className="font-medium text-slate-900">ensing & Metabolic Fuel-Gauging</span>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 text-indigo-600 font-bold text-xl">H</div>
              <div>
                <span className="font-medium text-slate-900">omeostatic Defence & Repair</span>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 text-indigo-600 font-bold text-xl">I</div>
              <div>
                <span className="font-medium text-slate-900">nflammation & Immune Tone</span>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 text-indigo-600 font-bold text-xl">F</div>
              <div>
                <span className="font-medium text-slate-900">ibrosis / Structural Remodeling</span>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 text-indigo-600 font-bold text-xl">T</div>
              <div>
                <span className="font-medium text-slate-900">imekeeping & Chronobiology</span>
              </div>
            </div>
          </div>
          
          <p className="text-slate-700">
            This presentation will analyze lifestyle interventions through these five pillars.
          </p>
          
          <ScientificDiagram 
            title="The SHIFT Framework" 
            caption="A unified lens to examine lifestyle's impact on cellular aging pathways"
          >
            <svg width="450" height="450" viewBox="0 0 450 450" xmlns="http://www.w3.org/2000/svg">
              {/* Central circle */}
              <circle cx="225" cy="225" r="60" fill="#e0e7ff" stroke="#818cf8" strokeWidth="2" />
              <text x="225" y="220" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="12">Lifestyle</text>
              <text x="225" y="235" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="12">Interventions</text>
              
              {/* Pentagon points */}
              <polygon 
                points="225,65 355,145 315,295 135,295 95,145" 
                fill="none" 
                stroke="#94a3b8" 
                strokeWidth="2" 
                strokeDasharray="5,5" 
              />
              
              {/* S: Sensing */}
              <circle cx="225" cy="65" r="50" fill="#dbeafe" stroke="#60a5fa" strokeWidth="2" />
              <text x="225" y="55" textAnchor="middle" fill="#1e40af" fontWeight="bold" fontSize="18">S</text>
              <text x="225" y="75" textAnchor="middle" fill="#1e40af" fontSize="10">Nutrient Sensing</text>
              <text x="225" y="90" textAnchor="middle" fill="#64748b" fontSize="8">Insulin/IGF-1, mTOR,</text>
              <text x="225" y="100" textAnchor="middle" fill="#64748b" fontSize="8">AMPK, Sirtuins</text>
              
              {/* H: Homeostatic */}
              <circle cx="355" cy="145" r="50" fill="#dcfce7" stroke="#4ade80" strokeWidth="2" />
              <text x="355" y="135" textAnchor="middle" fill="#166534" fontWeight="bold" fontSize="18">H</text>
              <text x="355" y="155" textAnchor="middle" fill="#166534" fontSize="10">Homeostatic Defence</text>
              <text x="355" y="170" textAnchor="middle" fill="#64748b" fontSize="8">NRF2, p53/p16,</text>
              <text x="355" y="180" textAnchor="middle" fill="#64748b" fontSize="8">Autophagy</text>
              
              {/* I: Inflammation */}
              <circle cx="315" cy="295" r="50" fill="#fee2e2" stroke="#f87171" strokeWidth="2" />
              <text x="315" y="285" textAnchor="middle" fill="#b91c1c" fontWeight="bold" fontSize="18">I</text>
              <text x="315" y="305" textAnchor="middle" fill="#b91c1c" fontSize="10">Inflammation</text>
              <text x="315" y="320" textAnchor="middle" fill="#64748b" fontSize="8">NF-κB, JAK-STAT,</text>
              <text x="315" y="330" textAnchor="middle" fill="#64748b" fontSize="8">SASP</text>
              
              {/* F: Fibrosis */}
              <circle cx="135" cy="295" r="50" fill="#fef3c7" stroke="#fbbf24" strokeWidth="2" />
              <text x="135" y="285" textAnchor="middle" fill="#b45309" fontWeight="bold" fontSize="18">F</text>
              <text x="135" y="305" textAnchor="middle" fill="#b45309" fontSize="10">Structural Integrity</text>
              <text x="135" y="320" textAnchor="middle" fill="#64748b" fontSize="8">TGF-β, WNT,</text>
              <text x="135" y="330" textAnchor="middle" fill="#64748b" fontSize="8">ECM Cross-linking</text>
              
              {/* T: Timekeeping */}
              <circle cx="95" cy="145" r="50" fill="#f3e8ff" stroke="#c084fc" strokeWidth="2" />
              <text x="95" y="135" textAnchor="middle" fill="#7e22ce" fontWeight="bold" fontSize="18">T</text>
              <text x="95" y="155" textAnchor="middle" fill="#7e22ce" fontSize="10">Timekeeping</text>
              <text x="95" y="170" textAnchor="middle" fill="#64748b" fontSize="8">CLOCK/BMAL1,</text>
              <text x="95" y="180" textAnchor="middle" fill="#64748b" fontSize="8">Epigenetic Clocks</text>
            </svg>
          </ScientificDiagram>
        </div>
      )
    },
    
    // Slide 5: Lecture Goals for Clinicians
    {
      id: 5,
      title: "What You Will Gain From This Session",
      content: (
        <div className="space-y-6">
          <ul className="space-y-4">
            <li className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              </div>
              <div className="text-slate-700">
                An evidence-saturated, digestible understanding of how to leverage lifestyle interventions.
              </div>
            </li>
            <li className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              </div>
              <div className="text-slate-700">
                Insight into the mechanisms by which nutrition, exercise, and sleep promote healthspan.
              </div>
            </li>
            <li className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              </div>
              <div className="text-slate-700">
                Actionable strategies and practical protocols for patient care.
              </div>
            </li>
            <li className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              </div>
              <div className="text-slate-700">
                Key biomarkers to consider for monitoring interventions.
              </div>
            </li>
          </ul>
          
          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-100 rounded-lg p-5 mt-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-indigo-100 rounded-full p-2 mr-4">
                <Users className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-base font-medium text-slate-900">Target Audience</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Physicians new to longevity medicine seeking evidence-based lifestyle interventions for clinical practice.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    
    // Slide 6: Introduction to Sensing & Metabolic Fuel-Gauging
    {
      id: 6,
      title: "SHIFT Framework: Sensing & Metabolic Fuel-Gauging",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700">
            The "Sensing" pillar refers to the intricate cellular systems that detect and respond to the availability of nutrients and energy status, fundamentally influencing metabolic health, aging, and longevity.
          </p>
          
          <ScientificDiagram 
            title="Sensing & Metabolic Fuel-Gauging Overview" 
            caption="Key nutrient-sensing pathways and their roles in cellular metabolism and longevity"
          >
            <svg width="450" height="350" viewBox="0 0 450 350" xmlns="http://www.w3.org/2000/svg">
              {/* Central hub */}
              <circle cx="225" cy="175" r="60" fill="#e0e7ff" stroke="#818cf8" strokeWidth="2" />
              <text x="225" y="165" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="14">Sensing &</text>
              <text x="225" y="185" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="14">Metabolic</text>
              <text x="225" y="205" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="14">Fuel-Gauging</text>
              
              {/* Pathway nodes */}
              <circle cx="100" cy="80" r="45" fill="#dbeafe" stroke="#60a5fa" strokeWidth="2" />
              <text x="100" y="75" textAnchor="middle" fill="#1e40af" fontWeight="bold" fontSize="12">Insulin/IGF-1</text>
              <text x="100" y="90" textAnchor="middle" fill="#64748b" fontSize="10">Growth & Anabolism</text>
              
              <circle cx="350" cy="80" r="45" fill="#dcfce7" stroke="#4ade80" strokeWidth="2" />
              <text x="350" y="75" textAnchor="middle" fill="#166534" fontWeight="bold" fontSize="12">mTOR</text>
              <text x="350" y="90" textAnchor="middle" fill="#64748b" fontSize="10">Protein Synthesis</text>
              
              <circle cx="100" cy="270" r="45" fill="#fee2e2" stroke="#f87171" strokeWidth="2" />
              <text x="100" y="265" textAnchor="middle" fill="#b91c1c" fontWeight="bold" fontSize="12">AMPK</text>
              <text x="100" y="280" textAnchor="middle" fill="#64748b" fontSize="10">Energy Sensor</text>
              
              <circle cx="350" cy="270" r="45" fill="#fef3c7" stroke="#fbbf24" strokeWidth="2" />
              <text x="350" y="265" textAnchor="middle" fill="#b45309" fontWeight="bold" fontSize="12">Sirtuins</text>
              <text x="350" y="280" textAnchor="middle" fill="#64748b" fontSize="10">NAD+ Dependent</text>
              
              {/* Connection lines */}
              <line x1="135" y1="105" x2="185" y2="145" stroke="#94a3b8" strokeWidth="2" />
              <line x1="315" y1="105" x2="265" y2="145" stroke="#94a3b8" strokeWidth="2" />
              <line x1="135" y1="245" x2="185" y2="205" stroke="#94a3b8" strokeWidth="2" />
              <line x1="315" y1="245" x2="265" y2="205" stroke="#94a3b8" strokeWidth="2" />
              
              {/* Nutrient status indicators */}
              <rect x="50" y="20" width="100" height="30" rx="15" fill="#bfdbfe" />
              <text x="100" y="40" textAnchor="middle" fill="#1e40af" fontSize="12">Nutrient Abundance</text>
              
              <rect x="300" y="20" width="100" height="30" rx="15" fill="#bfdbfe" />
              <text x="350" y="40" textAnchor="middle" fill="#1e40af" fontSize="12">Amino Acids</text>
              
              <rect x="50" y="300" width="100" height="30" rx="15" fill="#dcfce7" />
              <text x="100" y="320" textAnchor="middle" fill="#166534" fontSize="12">Energy Deficit</text>
              
              <rect x="300" y="300" width="100" height="30" rx="15" fill="#dcfce7" />
              <text x="350" y="320" textAnchor="middle" fill="#166534" fontSize="12">NAD+/NADH Ratio</text>
            </svg>
          </ScientificDiagram>
          
          <KeyTakeaway>
            Nutrient sensing pathways form an interconnected network that dictates cellular responses to feeding, fasting, and stress. Their modulation through lifestyle interventions is a key mechanism for promoting healthspan and longevity.
          </KeyTakeaway>
        </div>
      )
    },
    
    // Slide 7: Core Sensing Pathways
    {
      id: 7,
      title: "Core Sensing Pathways and Their Significance",
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h3 className="text-blue-800 font-medium mb-2">Insulin/IGF-1 Signaling (IIS)</h3>
              <p className="text-sm text-slate-700">
                Responds to glucose and amino acids, promoting growth and anabolism. Downregulation of IIS activity has consistently extended lifespan in model organisms. Exercise enhances insulin sensitivity, counteracting age-related insulin resistance.
              </p>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <h3 className="text-green-800 font-medium mb-2">mTOR (mechanistic Target of Rapamycin)</h3>
              <p className="text-sm text-slate-700">
                Central nutrient sensor integrating signals from growth factors, amino acids, and energy levels. Promotes anabolic processes and inhibits autophagy. Inhibition of mTOR (via rapamycin or caloric restriction) extends lifespan across species. Endurance exercise can inhibit mTORC1.
              </p>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <h3 className="text-red-800 font-medium mb-2">AMPK (AMP-activated Protein Kinase)</h3>
              <p className="text-sm text-slate-700">
                Critical energy sensor activated during states of low energy (high AMP/ATP ratio). Switches on catabolic pathways that generate ATP while switching off anabolic pathways. Promotes cellular stress resistance and longevity. Exercise is a well-established AMPK activator.
              </p>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="text-amber-800 font-medium mb-2">Sirtuins (SIRT1-7)</h3>
              <p className="text-sm text-slate-700">
                NAD+-dependent protein deacetylases crucial for regulating cellular health, metabolism, and aging. Activated by conditions that increase NAD+ levels, such as caloric restriction and exercise. Influence gene expression, mitochondrial function, and inflammation.
              </p>
            </div>
          </div>
          
          <p className="text-slate-700 text-sm italic">
            These pathways form an interconnected network: nutrient abundance activates IIS and mTOR (growth and storage), while nutrient scarcity activates AMPK and Sirtuins (energy generation and maintenance).
          </p>
        </div>
      )
    },
    
    // Slide 8: Nutritional Modulation of Sensing Pathways
    {
      id: 8,
      title: "Nutritional Modulation of Sensing Pathways",
      content: (
        <div className="space-y-6">
          <ScientificDiagram 
            title="Dietary Interventions & Sensing Pathways" 
            caption="How different nutritional strategies impact key nutrient-sensing pathways"
          >
            <svg width="450" height="400" viewBox="0 0 450 400" xmlns="http://www.w3.org/2000/svg">
              {/* Central vertical line */}
              <line x1="225" y1="50" x2="225" y2="350" stroke="#94a3b8" strokeWidth="2" strokeDasharray="5,5" />
              
              {/* Caloric Restriction */}
              <rect x="25" y="50" width="180" height="80" rx="10" fill="#e0e7ff" stroke="#818cf8" strokeWidth="2" />
              <text x="115" y="75" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="14">Caloric Restriction</text>
              <text x="115" y="95" textAnchor="middle" fill="#64748b" fontSize="10">15-40% calorie deficit</text>
              <text x="115" y="115" textAnchor="middle" fill="#4338ca" fontSize="10">↓IIS  ↓mTOR  ↑AMPK  ↑Sirtuins</text>
              
              {/* Intermittent Fasting */}
              <rect x="245" y="50" width="180" height="80" rx="10" fill="#dbeafe" stroke="#60a5fa" strokeWidth="2" />
              <text x="335" y="75" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="14">Intermittent Fasting</text>
              <text x="335" y="95" textAnchor="middle" fill="#64748b" fontSize="10">TRE, 5:2, ADF</text>
              <text x="335" y="115" textAnchor="middle" fill="#1d4ed8" fontSize="10">↓IIS  ↓mTOR  ↑AMPK  ↑Sirtuins</text>
              
              {/* Ketogenic Diet */}
              <rect x="25" y="150" width="180" height="80" rx="10" fill="#dcfce7" stroke="#4ade80" strokeWidth="2" />
              <text x="115" y="175" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="14">Ketogenic Diet</text>
              <text x="115" y="195" textAnchor="middle" fill="#64748b" fontSize="10">Very low-carb, high-fat</text>
              <text x="115" y="215" textAnchor="middle" fill="#166534" fontSize="10">↓IIS  Modulates mTOR  ↑BHB</text>
              
              {/* Omega-3 PUFAs */}
              <rect x="245" y="150" width="180" height="80" rx="10" fill="#fee2e2" stroke="#f87171" strokeWidth="2" />
              <text x="335" y="175" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="14">Omega-3 PUFAs</text>
              <text x="335" y="195" textAnchor="middle" fill="#64748b" fontSize="10">EPA/DHA from fatty fish, algae</text>
              <text x="335" y="215" textAnchor="middle" fill="#b91c1c" fontSize="10">↑Insulin Sensitivity  ↓Inflammation</text>
              
              {/* Meal Timing */}
              <rect x="25" y="250" width="180" height="80" rx="10" fill="#fef3c7" stroke="#fbbf24" strokeWidth="2" />
              <text x="115" y="275" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="14">Meal Timing</text>
              <text x="115" y="295" textAnchor="middle" fill="#64748b" fontSize="10">Chrononutrition</text>
              <text x="115" y="315" textAnchor="middle" fill="#b45309" fontSize="10">Optimizes IIS responsiveness</text>
              
              {/* Biomarkers */}
              <rect x="245" y="250" width="180" height="80" rx="10" fill="#f3e8ff" stroke="#c084fc" strokeWidth="2" />
              <text x="335" y="275" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="14">Key Biomarkers</text>
              <text x="335" y="295" textAnchor="middle" fill="#64748b" fontSize="10">Glucose, Insulin, HOMA-IR</text>
              <text x="335" y="315" textAnchor="middle" fill="#7e22ce" fontSize="10">Lipids, Ketones, Omega-3 Index</text>
            </svg>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h3 className="text-indigo-800 font-medium mb-2 text-sm">Clinical Protocols</h3>
              <ul className="text-xs text-slate-700 space-y-2">
                <li><span className="font-medium">TRE:</span> 14-16h fast daily (e.g., 16/8)</li>
                <li><span className="font-medium">5:2 Diet:</span> 2 days/week at 500-600 kcal</li>
                <li><span className="font-medium">Ketogenic:</span> &lt;50g CHO/day, monitor ketones</li>
                <li><span className="font-medium">Omega-3:</span> 1-2g EPA+DHA daily, aim for Index &gt;8%</li>
                <li><span className="font-medium">Meal Timing:</span> Larger meals earlier in day</li>
              </ul>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-blue-800 font-medium mb-2 text-sm">Evidence Strength</h3>
              <ul className="text-xs text-slate-700 space-y-2">
                <li><span className="font-medium">CR:</span> ★★★★ (animal), ★★★ (human biomarkers)</li>
                <li><span className="font-medium">IF:</span> ★★★ (human metabolic outcomes)</li>
                <li><span className="font-medium">KD:</span> ★★★★ (epilepsy), ★★★ (T2D)</li>
                <li><span className="font-medium">Omega-3:</span> ★★★★ (CVD, inflammation)</li>
                <li><span className="font-medium">Meal Timing:</span> ★★-★★★ (emerging evidence)</li>
              </ul>
            </div>
          </div>
          
          <KeyTakeaway>
            Nutritional interventions that create metabolic switching between fed and fasted states (CR, IF) or alter macronutrient composition (KD) are powerful modulators of sensing pathways. Timing of food intake and specific nutrients like omega-3s provide additional levers for optimizing metabolic health.
          </KeyTakeaway>
        </div>
      )
    },
    
    // Slide 9: Exercise Modulation of Sensing Pathways
    {
      id: 9,
      title: "Exercise Modulation of Sensing Pathways",
      content: (
        <div className="space-y-6">
          <ScientificDiagram 
            title="Exercise Effects on Sensing Pathways" 
            caption="How different exercise modalities impact key nutrient-sensing pathways"
          >
            <svg width="450" height="350" viewBox="0 0 450 350" xmlns="http://www.w3.org/2000/svg">
              {/* Central exercise figure */}
              <circle cx="225" cy="175" r="60" fill="#e0e7ff" stroke="#818cf8" strokeWidth="2" />
              <text x="225" y="165" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="14">Exercise</text>
              <text x="225" y="185" textAnchor="middle" fill="#1e293b" fontSize="12">Modulation of</text>
              <text x="225" y="205" textAnchor="middle" fill="#1e293b" fontSize="12">Sensing Pathways</text>
              
              {/* Exercise types */}
              <g transform="translate(0,-15)">
                {/* HIIT */}
                <rect x="50" y="65" width="120" height="60" rx="10" fill="#dbeafe" stroke="#60a5fa" strokeWidth="2" />
                <text x="110" y="90" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="12">HIIT</text>
                <text x="110" y="110" textAnchor="middle" fill="#1d4ed8" fontSize="10">↑↑AMPK, ↑Sirtuins</text>
                
                {/* Resistance */}
                <rect x="280" y="65" width="120" height="60" rx="10" fill="#dcfce7" stroke="#4ade80" strokeWidth="2" />
                <text x="340" y="90" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="12">Resistance</text>
                <text x="340" y="110" textAnchor="middle" fill="#166534" fontSize="10">↑mTOR (acute), ↑Sirtuins</text>
                
                {/* Endurance */}
                <rect x="50" y="225" width="120" height="60" rx="10" fill="#fee2e2" stroke="#f87171" strokeWidth="2" />
                <text x="110" y="250" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="12">Endurance</text>
                <text x="110" y="270" textAnchor="middle" fill="#b91c1c" fontSize="10">↑AMPK, ↓basal mTOR</text>
                
                {/* NEAT */}
                <rect x="280" y="225" width="120" height="60" rx="10" fill="#fef3c7" stroke="#fbbf24" strokeWidth="2" />
                <text x="340" y="250" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="12">NEAT</text>
                <text x="340" y="270" textAnchor="middle" fill="#b45309" fontSize="10">Improved metabolic tone</text>
              </g>
              
              {/* Connecting lines */}
              <line x1="140" y1="95" x2="175" y2="145" stroke="#94a3b8" strokeWidth="2" />
              <line x1="310" y1="95" x2="275" y2="145" stroke="#94a3b8" strokeWidth="2" />
              <line x1="140" y1="255" x2="175" y2="205" stroke="#94a3b8" strokeWidth="2" />
              <line x1="310" y1="255" x2="275" y2="205" stroke="#94a3b8" strokeWidth="2" />
            </svg>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h3 className="text-indigo-800 font-medium mb-2 text-sm">Clinical Protocols</h3>
              <ul className="text-xs text-slate-700 space-y-2">
                <li><span className="font-medium">HIIT:</span> 4-6 x 30s sprints with 2-4min recovery, 2-3x/week</li>
                <li><span className="font-medium">Resistance:</span> 2-3 sessions/week, compound movements, 60-80% 1RM</li>
                <li><span className="font-medium">Endurance (Zone 2):</span> 3-5 sessions/week at 60-70% HRmax</li>
                <li><span className="font-medium">NEAT:</span> 7,500-10,000+ steps/day, regular movement breaks</li>
              </ul>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-blue-800 font-medium mb-2 text-sm">Key Biomarkers</h3>
              <ul className="text-xs text-slate-700 space-y-2">
                <li><span className="font-medium">Fitness:</span> VO2max, strength (1RM)</li>
                <li><span className="font-medium">Metabolic:</span> HOMA-IR, fasting glucose/insulin</li>
                <li><span className="font-medium">Body Composition:</span> Lean mass, visceral fat</li>
                <li><span className="font-medium">Research:</span> AMPK/mTOR/SIRT1 in muscle/PBMCs</li>
              </ul>
            </div>
          </div>
          
          <KeyTakeaway>
            Exercise is a powerful, pleiotropic intervention that robustly modulates all key nutrient-sensing pathways. Different modalities have complementary effects: HIIT/endurance strongly activate AMPK and Sirtuins, while resistance exercise acutely activates mTOR for muscle protein synthesis. A combined approach likely provides optimal benefits for metabolic health and longevity.
          </KeyTakeaway>
        </div>
      )
    },
    
    // Slide 10: Sleep & Chronobiology Modulation of Sensing Pathways
    {
      id: 10,
      title: "Sleep & Chronobiology Modulation of Sensing Pathways",
      content: (
        <div className="space-y-6">
          <ScientificDiagram 
            title="Sleep, Circadian Rhythms & Sensing Pathways" 
            caption="How sleep quality and timing impact key nutrient-sensing pathways"
          >
            <svg width="450" height="350" viewBox="0 0 450 350" xmlns="http://www.w3.org/2000/svg">
              {/* Background gradient */}
              <defs>
                <linearGradient id="sleepGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#f8fafc" />
                  <stop offset="100%" stopColor="#f1f5f9" />
                </linearGradient>
              </defs>
              <rect x="0" y="0" width="450" height="350" fill="url(#sleepGradient)" rx="10" />
              
              {/* Day-night cycle */}
              <circle cx="225" cy="80" r="50" fill="#fef3c7" stroke="#fbbf24" strokeWidth="2" />
              <path d="M225,30 A50,50 0 0,1 225,130 A50,50 0 0,1 225,30" fill="#1e293b" />
              
              {/* Sleep quality box */}
              <rect x="75" y="160" width="150" height="120" rx="10" fill="#e0e7ff" stroke="#818cf8" strokeWidth="2" />
              <text x="150" y="180" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="14">Sleep Quality</text>
              <line x1="100" y1="195" x2="200" y2="195" stroke="#94a3b8" strokeWidth="1" />
              <text x="150" y="215" textAnchor="middle" fill="#1e293b" fontSize="12">Poor Sleep:</text>
              <text x="150" y="235" textAnchor="middle" fill="#dc2626" fontSize="10">↑Insulin Resistance</text>
              <text x="150" y="255" textAnchor="middle" fill="#dc2626" fontSize="10">↓Sirtuin/Klotho</text>
              
              {/* Circadian alignment box */}
              <rect x="275" y="160" width="150" height="120" rx="10" fill="#dcfce7" stroke="#4ade80" strokeWidth="2" />
              <text x="350" y="180" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="14">Circadian Alignment</text>
              <line x1="300" y1="195" x2="400" y2="195" stroke="#94a3b8" strokeWidth="1" />
              <text x="350" y="215" textAnchor="middle" fill="#1e293b" fontSize="12">Misalignment:</text>
              <text x="350" y="235" textAnchor="middle" fill="#dc2626" fontSize="10">↓Metabolic Efficiency</text>
              <text x="350" y="255" textAnchor="middle" fill="#dc2626" fontSize="10">Disrupted mTOR/AMPK</text>
              
              {/* Connecting elements */}
              <line x1="225" y1="130" x2="150" y2="160" stroke="#94a3b8" strokeWidth="2" strokeDasharray="5,5" />
              <line x1="225" y1="130" x2="350" y2="160" stroke="#94a3b8" strokeWidth="2" strokeDasharray="5,5" />
              
              {/* Bottom recommendations */}
              <rect x="75" y="300" width="300" height="30" rx="15" fill="#f3e8ff" stroke="#c084fc" strokeWidth="2" />
              <text x="225" y="320" textAnchor="middle" fill="#1e293b" fontSize="12">7-9 hours quality sleep + consistent timing</text>
            </svg>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h3 className="text-indigo-800 font-medium mb-2 text-sm">Clinical Protocols</h3>
              <ul className="text-xs text-slate-700 space-y-2">
                <li><span className="font-medium">Sleep Hygiene:</span> Consistent schedule, optimal environment, limit stimulants</li>
                <li><span className="font-medium">Light Management:</span> Morning bright light, evening blue light reduction</li>
                <li><span className="font-medium">Melatonin:</span> Low dose (0.3-1mg) for circadian disorders, not first-line for insomnia</li>
                <li><span className="font-medium">CBT-I:</span> Gold standard for chronic insomnia</li>
              </ul>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-blue-800 font-medium mb-2 text-sm">Key Biomarkers</h3>
              <ul className="text-xs text-slate-700 space-y-2">
                <li><span className="font-medium">Sleep:</span> PSG (sleep architecture), actigraphy (patterns)</li>
                <li><span className="font-medium">Metabolic:</span> Fasting glucose, insulin, HOMA-IR, OGTT</li>
                <li><span className="font-medium">Hormonal:</span> Cortisol rhythm, melatonin profile (DLMO)</li>
                <li><span className="font-medium">Research:</span> Circulating Klotho, Sirtuin activity</li>
              </ul>
            </div>
          </div>
          
          <KeyTakeaway>
            Sleep is not merely a passive state but an active period for metabolic regulation and cellular repair. Both sleep quality and circadian alignment significantly impact nutrient-sensing pathways. Sleep deprivation impairs insulin sensitivity and can disrupt Sirtuin/Klotho homeostasis, while circadian misalignment disrupts the normal oscillation of metabolic enzymes and sensing pathways, contributing to metabolic dysfunction.
          </KeyTakeaway>
        </div>
      )
    },
    
    // Slide 11: Hormesis - Stress Response Pathways
    {
      id: 11,
      title: "Hormesis - Stress Response Pathways",
      content: (
        <div className="space-y-6">
          <ScientificDiagram 
            title="Hormetic Stress Response" 
            caption="How controlled stressors activate adaptive cellular pathways"
          >
            <svg width="450" height="350" viewBox="0 0 450 350" xmlns="http://www.w3.org/2000/svg">
              {/* Background */}
              <rect x="0" y="0" width="450" height="350" fill="#f8fafc" rx="10" />
              
              {/* Hormetic curve */}
              <path d="M50,250 Q125,50 225,175 Q325,300 400,100" stroke="#6366f1" strokeWidth="3" fill="none" />
              
              {/* Axes */}
              <line x1="50" y1="50" x2="50" y2="300" stroke="#334155" strokeWidth="2" />
              <line x1="50" y1="300" x2="400" y2="300" stroke="#334155" strokeWidth="2" />
              
              {/* Axis labels */}
              <text x="225" y="330" textAnchor="middle" fill="#334155" fontSize="12">Stress Intensity</text>
              <text x="30" y="175" textAnchor="middle" fill="#334155" fontSize="12" transform="rotate(-90,30,175)">Adaptive Response</text>
              
              {/* Optimal zone */}
              <rect x="150" y="100" width="150" height="200" fill="#a5b4fc" fillOpacity="0.2" rx="5" />
              <text x="225" y="90" textAnchor="middle" fill="#4338ca" fontSize="12" fontWeight="bold">Hormetic Zone</text>
              
              {/* Stress examples */}
              <circle cx="100" cy="250" r="15" fill="#bfdbfe" stroke="#3b82f6" strokeWidth="1" />
              <text x="100" y="254" textAnchor="middle" fill="#1e40af" fontSize="8">Mild</text>
              
              <circle cx="225" cy="175" r="15" fill="#c7d2fe" stroke="#6366f1" strokeWidth="1" />
              <text x="225" y="179" textAnchor="middle" fill="#3730a3" fontSize="8">Optimal</text>
              
              <circle cx="350" cy="200" r="15" fill="#fecaca" stroke="#ef4444" strokeWidth="1" />
              <text x="350" y="204" textAnchor="middle" fill="#b91c1c" fontSize="8">Excess</text>
              
              {/* Pathway activation indicators */}
              <g transform="translate(0,20)">
                <rect x="50" y="250" width="100" height="25" rx="5" fill="#dbeafe" />
                <text x="100" y="267" textAnchor="middle" fill="#1e40af" fontSize="10">Minimal Activation</text>
                
                <rect x="175" y="250" width="100" height="25" rx="5" fill="#c7d2fe" />
                <text x="225" y="267" textAnchor="middle" fill="#3730a3" fontSize="10">NRF2, FOXO, PGC-1α</text>
                
                <rect x="300" y="250" width="100" height="25" rx="5" fill="#fecaca" />
                <text x="350" y="267" textAnchor="middle" fill="#b91c1c" fontSize="10">Inflammation, ROS</text>
              </g>
            </svg>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h3 className="text-indigo-800 font-medium mb-2 text-sm">Clinical Applications</h3>
              <ul className="text-xs text-slate-700 space-y-2">
                <li><span className="font-medium">Heat Exposure:</span> Sauna (20-30min at 80-100°C, 2-3x/week)</li>
                <li><span className="font-medium">Cold Exposure:</span> Cold immersion (2-5min at 10-15°C, 2-3x/week)</li>
                <li><span className="font-medium">Phytochemicals:</span> Sulforaphane, resveratrol, curcumin</li>
                <li><span className="font-medium">Exercise:</span> HIIT, resistance training to hormetic threshold</li>
              </ul>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-blue-800 font-medium mb-2 text-sm">Key Mechanisms</h3>
              <ul className="text-xs text-slate-700 space-y-2">
                <li><span className="font-medium">NRF2 Activation:</span> Antioxidant response, detoxification</li>
                <li><span className="font-medium">Heat Shock Proteins:</span> Protein folding, cellular protection</li>
                <li><span className="font-medium">Mitohormesis:</span> Mitochondrial biogenesis, efficiency</li>
                <li><span className="font-medium">Xenohormesis:</span> Plant stress compounds activating human pathways</li>
              </ul>
            </div>
          </div>
          
          <KeyTakeaway>
            Hormesis describes the beneficial adaptive response to low-dose stressors that would be harmful at higher doses. This biphasic dose-response relationship is central to many longevity interventions. Controlled exposure to specific stressors (heat, cold, certain plant compounds, exercise) activates stress response pathways like NRF2, FOXO, and heat shock proteins, improving cellular resilience, reducing inflammation, and enhancing metabolic efficiency. The key is finding the optimal "hormetic zone" where stress is sufficient to trigger adaptation without causing damage.
          </KeyTakeaway>
        </div>
      )
    },
    
    // Slide 12: Fasting & Time-Restricted Eating
    {
      id: 12,
      title: "Fasting & Time-Restricted Eating",
      content: (
        <div className="space-y-6">
          <ScientificDiagram 
            title="Fasting Protocols & Metabolic Effects" 
            caption="Comparing different fasting approaches and their impact on metabolic pathways"
          >
            <svg width="450" height="350" viewBox="0 0 450 350" xmlns="http://www.w3.org/2000/svg">
              {/* Background */}
              <rect x="0" y="0" width="450" height="350" fill="#f8fafc" rx="10" />
              
              {/* Timeline bar */}
              <rect x="50" y="50" width="350" height="40" fill="#e2e8f0" rx="5" />
              
              {/* Timeline markers */}
              <line x1="50" y1="100" x2="50" y2="90" stroke="#334155" strokeWidth="2" />
              <text x="50" y="115" textAnchor="middle" fill="#334155" fontSize="10">0h</text>
              
              <line x1="125" y1="100" x2="125" y2="90" stroke="#334155" strokeWidth="2" />
              <text x="125" y="115" textAnchor="middle" fill="#334155" fontSize="10">12h</text>
              
              <line x1="200" y1="100" x2="200" y2="90" stroke="#334155" strokeWidth="2" />
              <text x="200" y="115" textAnchor="middle" fill="#334155" fontSize="10">24h</text>
              
              <line x1="275" y1="100" x2="275" y2="90" stroke="#334155" strokeWidth="2" />
              <text x="275" y="115" textAnchor="middle" fill="#334155" fontSize="10">36h</text>
              
              <line x1="350" y1="100" x2="350" y2="90" stroke="#334155" strokeWidth="2" />
              <text x="350" y="115" textAnchor="middle" fill="#334155" fontSize="10">48h</text>
              
              {/* Fasting protocols */}
              <g transform="translate(0,20)">
                {/* TRE */}
                <rect x="50" y="120" width="75" height="30" rx="5" fill="#dbeafe" stroke="#3b82f6" strokeWidth="1" />
                <text x="87" y="140" textAnchor="middle" fill="#1e40af" fontSize="10">TRE (16:8)</text>
                
                {/* 24h */}
                <rect x="50" y="160" width="150" height="30" rx="5" fill="#c7d2fe" stroke="#6366f1" strokeWidth="1" />
                <text x="125" y="180" textAnchor="middle" fill="#3730a3" fontSize="10">24h Fast</text>
                
                {/* ADF */}
                <rect x="50" y="200" width="300" height="30" rx="5" fill="#e0e7ff" stroke="#818cf8" strokeWidth="1" />
                <text x="200" y="220" textAnchor="middle" fill="#4338ca" fontSize="10">Alternate Day Fast</text>
                
                {/* Extended */}
                <rect x="50" y="240" width="300" height="30" rx="5" fill="#ede9fe" stroke="#a78bfa" strokeWidth="1" />
                <text x="200" y="260" textAnchor="middle" fill="#5b21b6" fontSize="10">Extended Fast (36-48h)</text>
              </g>
              
              {/* Metabolic phases */}
              <line x1="80" y1="50" x2="80" y2="290" stroke="#94a3b8" strokeWidth="1" strokeDasharray="4,4" />
              <text x="80" y="305" textAnchor="middle" fill="#475569" fontSize="9">Fed</text>
              
              <line x1="125" y1="50" x2="125" y2="290" stroke="#94a3b8" strokeWidth="1" strokeDasharray="4,4" />
              <text x="125" y="305" textAnchor="middle" fill="#475569" fontSize="9">Post-absorptive</text>
              
              <line x1="200" y1="50" x2="200" y2="290" stroke="#94a3b8" strokeWidth="1" strokeDasharray="4,4" />
              <text x="200" y="305" textAnchor="middle" fill="#475569" fontSize="9">Gluconeogenesis</text>
              
              <line x1="275" y1="50" x2="275" y2="290" stroke="#94a3b8" strokeWidth="1" strokeDasharray="4,4" />
              <text x="275" y="305" textAnchor="middle" fill="#475569" fontSize="9">Ketosis</text>
              
              {/* Pathway activation */}
              <rect x="50" y="320" width="350" height="20" fill="url(#fastingGradient)" rx="5" />
              <defs>
                <linearGradient id="fastingGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#bfdbfe" />
                  <stop offset="30%" stopColor="#93c5fd" />
                  <stop offset="60%" stopColor="#6366f1" />
                  <stop offset="100%" stopColor="#4338ca" />
                </linearGradient>
              </defs>
              <text x="225" y="334" textAnchor="middle" fill="#f8fafc" fontSize="10">↑AMPK/Autophagy ↓mTOR/Insulin</text>
            </svg>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h3 className="text-indigo-800 font-medium mb-2 text-sm">Clinical Protocols</h3>
              <ul className="text-xs text-slate-700 space-y-2">
                <li><span className="font-medium">Time-Restricted Eating:</span> 16:8 or 14:10 daily pattern</li>
                <li><span className="font-medium">Intermittent Fasting:</span> 24h fast 1-2x/week (dinner to dinner)</li>
                <li><span className="font-medium">5:2 Protocol:</span> 500-600 calories on 2 non-consecutive days/week</li>
                <li><span className="font-medium">Extended Fasting:</span> 36-72h quarterly with medical supervision</li>
              </ul>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-blue-800 font-medium mb-2 text-sm">Metabolic Effects</h3>
              <ul className="text-xs text-slate-700 space-y-2">
                <li><span className="font-medium">12-16h:</span> Glycogen depletion, ↑lipolysis, ↑AMPK activation</li>
                <li><span className="font-medium">16-24h:</span> ↑Ketone production, ↑autophagy, ↓inflammation</li>
                <li><span className="font-medium">24-48h:</span> ↑↑Autophagy, ↑ketosis, ↑BDNF, immune remodeling</li>
                <li><span className="font-medium">48h+:</span> ↑↑↑Autophagy, stem cell activation, significant immune effects</li>
              </ul>
            </div>
          </div>
          
          <KeyTakeaway>
            Fasting interventions represent a spectrum of approaches that leverage evolutionary adaptation to food scarcity. Time-restricted eating (16:8) offers metabolic benefits with minimal disruption to daily life, while longer fasts (24-48h) induce more profound autophagy and ketosis. All fasting protocols modulate key nutrient-sensing pathways: reducing insulin/IGF-1/mTOR signaling while activating AMPK/Sirtuins/autophagy. Clinical implementation should be personalized based on patient health status, goals, and metabolic flexibility, with appropriate medical supervision for extended protocols.
          </KeyTakeaway>
        </div>
      )
    },
    
    // Slide 13: Nutritional Approaches for Metabolic Health
    {
      id: 13,
      title: "Nutritional Approaches for Metabolic Health",
      content: (
        <div className="space-y-6">
          <ScientificDiagram 
            title="Nutritional Strategies & Metabolic Pathways" 
            caption="Comparing dietary approaches and their impact on key metabolic pathways"
          >
            <svg width="450" height="350" viewBox="0 0 450 350" xmlns="http://www.w3.org/2000/svg">
              {/* Background */}
              <rect x="0" y="0" width="450" height="350" fill="#f8fafc" rx="10" />
              
              {/* Central plate */}
              <circle cx="225" cy="175" r="100" fill="#f1f5f9" stroke="#64748b" strokeWidth="1" />
              <circle cx="225" cy="175" r="70" fill="#f8fafc" stroke="#64748b" strokeWidth="1" />
              
              {/* Dietary patterns */}
              <g transform="translate(0,-10)">
                {/* Mediterranean */}
                <path d="M225,175 L225,75 A100,100 0 0,1 308,113 Z" fill="#dcfce7" stroke="#4ade80" strokeWidth="1" />
                <text x="265" y="105" textAnchor="middle" fill="#166534" fontSize="10" fontWeight="bold">Mediterranean</text>
                
                {/* Low-Carb */}
                <path d="M225,175 L308,113 A100,100 0 0,1 308,237 Z" fill="#dbeafe" stroke="#60a5fa" strokeWidth="1" />
                <text x="315" y="175" textAnchor="middle" fill="#1e40af" fontSize="10" fontWeight="bold">Low-Carb</text>
                
                {/* Plant-Based */}
                <path d="M225,175 L308,237 A100,100 0 0,1 225,275 Z" fill="#e0e7ff" stroke="#818cf8" strokeWidth="1" />
                <text x="265" y="245" textAnchor="middle" fill="#3730a3" fontSize="10" fontWeight="bold">Plant-Based</text>
                
                {/* Paleo/Ancestral */}
                <path d="M225,175 L225,275 A100,100 0 0,1 142,237 Z" fill="#fef3c7" stroke="#fbbf24" strokeWidth="1" />
                <text x="185" y="245" textAnchor="middle" fill="#92400e" fontSize="10" fontWeight="bold">Ancestral</text>
                
                {/* Caloric Restriction */}
                <path d="M225,175 L142,237 A100,100 0 0,1 142,113 Z" fill="#fee2e2" stroke="#f87171" strokeWidth="1" />
                <text x="135" y="175" textAnchor="middle" fill="#b91c1c" fontSize="10" fontWeight="bold">Caloric</text>
                <text x="135" y="188" textAnchor="middle" fill="#b91c1c" fontSize="10" fontWeight="bold">Restriction</text>
                
                {/* Protein Optimization */}
                <path d="M225,175 L142,113 A100,100 0 0,1 225,75 Z" fill="#fae8ff" stroke="#d946ef" strokeWidth="1" />
                <text x="185" y="105" textAnchor="middle" fill="#a21caf" fontSize="10" fontWeight="bold">Protein</text>
                <text x="185" y="118" textAnchor="middle" fill="#a21caf" fontSize="10" fontWeight="bold">Optimization</text>
              </g>
              
              {/* Center text */}
              <text x="225" y="165" textAnchor="middle" fill="#334155" fontSize="12" fontWeight="bold">Metabolic</text>
              <text x="225" y="180" textAnchor="middle" fill="#334155" fontSize="12" fontWeight="bold">Health</text>
              <text x="225" y="195" textAnchor="middle" fill="#334155" fontSize="10">Pathway</text>
              <text x="225" y="210" textAnchor="middle" fill="#334155" fontSize="10">Modulation</text>
              
              {/* Key pathway effects */}
              <g transform="translate(0,20)">
                <rect x="50" y="290" width="350" height="30" rx="5" fill="#f1f5f9" stroke="#64748b" strokeWidth="1" />
                <text x="225" y="310" textAnchor="middle" fill="#334155" fontSize="10">All effective approaches: ↓Insulin/IGF-1, ↑AMPK, ↑Autophagy</text>
              </g>
            </svg>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h3 className="text-indigo-800 font-medium mb-2 text-sm">Common Principles</h3>
              <ul className="text-xs text-slate-700 space-y-2">
                <li><span className="font-medium">Whole Foods:</span> Minimally processed, nutrient-dense foods</li>
                <li><span className="font-medium">Protein Adequacy:</span> 1.2-2.0g/kg lean mass, quality sources</li>
                <li><span className="font-medium">Phytonutrients:</span> Diverse plant compounds (polyphenols, etc.)</li>
                <li><span className="font-medium">Metabolic Flexibility:</span> Varied fuel sources, periodic challenges</li>
              </ul>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-blue-800 font-medium mb-2 text-sm">Personalization Factors</h3>
              <ul className="text-xs text-slate-700 space-y-2">
                <li><span className="font-medium">Metabolic Status:</span> Insulin sensitivity, glycemic control</li>
                <li><span className="font-medium">Genetic Factors:</span> APOE status, lactase persistence, etc.</li>
                <li><span className="font-medium">Microbiome:</span> Personalized fiber/prebiotic responses</li>
                <li><span className="font-medium">Activity Level:</span> Exercise type, intensity, frequency</li>
              </ul>
            </div>
          </div>
          
          <KeyTakeaway>
            While dietary approaches vary in macronutrient composition and specific foods, all effective nutritional strategies for metabolic health and longevity share common principles: they reduce chronic hyperinsulinemia, optimize protein intake, provide diverse phytonutrients, and support metabolic flexibility. The optimal approach for an individual patient depends on metabolic status, genetics, gut microbiome, lifestyle factors, and personal preferences. Focus on shared principles rather than dogmatic adherence to a single dietary pattern.
          </KeyTakeaway>
        </div>
      )
    },
    
    // Slide 14: Toxin Avoidance & Environmental Factors
    {
      id: 14,
      title: "Toxin Avoidance & Environmental Factors",
      content: (
        <div className="space-y-6">
          <ScientificDiagram 
            title="Environmental Exposures & Metabolic Health" 
            caption="Key environmental factors that impact cellular health and longevity pathways"
          >
            <svg width="450" height="350" viewBox="0 0 450 350" xmlns="http://www.w3.org/2000/svg">
              {/* Background */}
              <rect x="0" y="0" width="450" height="350" fill="#f8fafc" rx="10" />
              
              {/* Central cell */}
              <circle cx="225" cy="175" r="60" fill="#f1f5f9" stroke="#64748b" strokeWidth="2" />
              <text x="225" y="165" textAnchor="middle" fill="#334155" fontSize="12" fontWeight="bold">Cellular</text>
              <text x="225" y="180" textAnchor="middle" fill="#334155" fontSize="12" fontWeight="bold">Health</text>
              <text x="225" y="195" textAnchor="middle" fill="#334155" fontSize="10">Pathway</text>
              <text x="225" y="210" textAnchor="middle" fill="#334155" fontSize="10">Disruption</text>
              
              {/* Environmental factors */}
              <g transform="translate(0,-10)">
                {/* Air Pollution */}
                <rect x="50" y="60" width="120" height="60" rx="10" fill="#fee2e2" stroke="#f87171" strokeWidth="2" />
                <text x="110" y="85" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="12">Air Pollution</text>
                <text x="110" y="105" textAnchor="middle" fill="#b91c1c" fontSize="10">↑Oxidative Stress, ↑NF-κB</text>
                
                {/* Endocrine Disruptors */}
                <rect x="280" y="60" width="120" height="60" rx="10" fill="#fef3c7" stroke="#fbbf24" strokeWidth="2" />
                <text x="340" y="85" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="12">Endocrine Disruptors</text>
                <text x="340" y="105" textAnchor="middle" fill="#92400e" fontSize="10">Hormonal Dysregulation</text>
                
                {/* Light Pollution */}
                <rect x="50" y="240" width="120" height="60" rx="10" fill="#dbeafe" stroke="#60a5fa" strokeWidth="2" />
                <text x="110" y="265" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="12">Light Pollution</text>
                <text x="110" y="285" textAnchor="middle" fill="#1e40af" fontSize="10">Circadian Disruption</text>
                
                {/* Heavy Metals */}
                <rect x="280" y="240" width="120" height="60" rx="10" fill="#e0e7ff" stroke="#818cf8" strokeWidth="2" />
                <text x="340" y="265" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="12">Heavy Metals</text>
                <text x="340" y="285" textAnchor="middle" fill="#3730a3" fontSize="10">Mitochondrial Damage</text>
              </g>
              
              {/* Connecting lines */}
              <line x1="140" y1="90" x2="175" y2="145" stroke="#ef4444" strokeWidth="2" strokeDasharray="5,3" />
              <line x1="310" y1="90" x2="275" y2="145" stroke="#f59e0b" strokeWidth="2" strokeDasharray="5,3" />
              <line x1="140" y1="250" x2="175" y2="205" stroke="#3b82f6" strokeWidth="2" strokeDasharray="5,3" />
              <line x1="310" y1="250" x2="275" y2="205" stroke="#6366f1" strokeWidth="2" strokeDasharray="5,3" />
            </svg>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h3 className="text-indigo-800 font-medium mb-2 text-sm">Key Exposures</h3>
              <ul className="text-xs text-slate-700 space-y-2">
                <li><span className="font-medium">Air Pollution:</span> PM2.5, ozone, VOCs, traffic-related pollution</li>
                <li><span className="font-medium">Endocrine Disruptors:</span> BPA, phthalates, PFAS, pesticides</li>
                <li><span className="font-medium">Light Pollution:</span> Blue light at night, shift work, jet lag</li>
                <li><span className="font-medium">Heavy Metals:</span> Lead, mercury, cadmium, arsenic</li>
              </ul>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-blue-800 font-medium mb-2 text-sm">Clinical Interventions</h3>
              <ul className="text-xs text-slate-700 space-y-2">
                <li><span className="font-medium">Air Quality:</span> HEPA filters, indoor plants, pollution monitoring</li>
                <li><span className="font-medium">Water:</span> Reverse osmosis/carbon filtration, glass containers</li>
                <li><span className="font-medium">Food:</span> Organic when possible, avoid plastic food storage</li>
                <li><span className="font-medium">Detoxification Support:</span> Optimize Phase I/II pathways, sweat therapy</li>
              </ul>
            </div>
          </div>
          
          <KeyTakeaway>
            Environmental toxins and exposures represent a significant but often overlooked factor in metabolic health and longevity. These exposures can disrupt key cellular pathways through oxidative stress, inflammation, hormonal dysregulation, and mitochondrial damage. Clinical interventions should focus on both reducing exposures (through air/water filtration, organic foods, etc.) and supporting the body's natural detoxification systems. Environmental medicine is an essential component of a comprehensive approach to longevity medicine.
          </KeyTakeaway>
        </div>
      )
    },
    
    // Slide 15: Mind-Body Connection & Stress Management
    {
      id: 15,
      title: "Mind-Body Connection & Stress Management",
      content: (
        <div className="space-y-6">
          <ScientificDiagram 
            title="Psychoneuroimmunology & Metabolic Health" 
            caption="How psychological factors impact physiological pathways relevant to longevity"
          >
            <svg width="450" height="350" viewBox="0 0 450 350" xmlns="http://www.w3.org/2000/svg">
              {/* Background */}
              <rect x="0" y="0" width="450" height="350" fill="#f8fafc" rx="10" />
              
              {/* Brain */}
              <ellipse cx="225" cy="80" rx="60" ry="40" fill="#e0e7ff" stroke="#818cf8" strokeWidth="2" />
              <text x="225" y="85" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="14">Mind</text>
              
              {/* Body Systems */}
              <rect x="75" y="180" width="100" height="60" rx="10" fill="#dbeafe" stroke="#60a5fa" strokeWidth="2" />
              <text x="125" y="200" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="12">Nervous</text>
              <text x="125" y="220" textAnchor="middle" fill="#1e293b" fontSize="12">System</text>
              
              <rect x="175" y="250" width="100" height="60" rx="10" fill="#dcfce7" stroke="#4ade80" strokeWidth="2" />
              <text x="225" y="270" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="12">Endocrine</text>
              <text x="225" y="290" textAnchor="middle" fill="#1e293b" fontSize="12">System</text>
              
              <rect x="275" y="180" width="100" height="60" rx="10" fill="#fee2e2" stroke="#f87171" strokeWidth="2" />
              <text x="325" y="200" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="12">Immune</text>
              <text x="325" y="220" textAnchor="middle" fill="#1e293b" fontSize="12">System</text>
              
              {/* Connecting pathways */}
              <path d="M225,120 C225,120 175,150 125,180" stroke="#94a3b8" strokeWidth="2" fill="none" />
              <path d="M225,120 C225,120 225,200 225,250" stroke="#94a3b8" strokeWidth="2" fill="none" />
              <path d="M225,120 C225,120 275,150 325,180" stroke="#94a3b8" strokeWidth="2" fill="none" />
              
              {/* Cross-connections */}
              <path d="M125,240 C125,270 175,270 225,270" stroke="#94a3b8" strokeWidth="2" fill="none" strokeDasharray="5,3" />
              <path d="M325,240 C325,270 275,270 225,270" stroke="#94a3b8" strokeWidth="2" fill="none" strokeDasharray="5,3" />
              <path d="M125,210 C175,210 275,210 325,210" stroke="#94a3b8" strokeWidth="2" fill="none" strokeDasharray="5,3" />
              
              {/* Key mediators */}
              <circle cx="175" cy="150" r="15" fill="#fef3c7" stroke="#fbbf24" strokeWidth="1" />
              <text x="175" y="154" textAnchor="middle" fill="#92400e" fontSize="8">HPA Axis</text>
              
              <circle cx="275" cy="150" r="15" fill="#fef3c7" stroke="#fbbf24" strokeWidth="1" />
              <text x="275" y="154" textAnchor="middle" fill="#92400e" fontSize="8">Cytokines</text>
              
              <circle cx="225" cy="210" r="15" fill="#fef3c7" stroke="#fbbf24" strokeWidth="1" />
              <text x="225" y="214" textAnchor="middle" fill="#92400e" fontSize="8">Vagus</text>
            </svg>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h3 className="text-indigo-800 font-medium mb-2 text-sm">Key Mechanisms</h3>
              <ul className="text-xs text-slate-700 space-y-2">
                <li><span className="font-medium">HPA Axis:</span> Cortisol dysregulation, allostatic load</li>
                <li><span className="font-medium">Inflammation:</span> Chronic elevation of pro-inflammatory cytokines</li>
                <li><span className="font-medium">Vagal Tone:</span> Autonomic balance, parasympathetic activation</li>
                <li><span className="font-medium">Telomeres:</span> Accelerated telomere shortening with chronic stress</li>
              </ul>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-blue-800 font-medium mb-2 text-sm">Clinical Interventions</h3>
              <ul className="text-xs text-slate-700 space-y-2">
                <li><span className="font-medium">Mindfulness:</span> MBSR, meditation (20min daily), present-moment awareness</li>
                <li><span className="font-medium">Breathwork:</span> Resonance breathing (6 breaths/min), HRV biofeedback</li>
                <li><span className="font-medium">Social Connection:</span> Quality relationships, community engagement</li>
                <li><span className="font-medium">Purpose:</span> Meaning, values alignment, flow states, eudaimonic wellbeing</li>
              </ul>
            </div>
          </div>
          
          <KeyTakeaway>
            The mind-body connection represents a powerful pathway through which psychological states influence physiological processes relevant to longevity. Chronic psychological stress activates inflammatory pathways, dysregulates the HPA axis, and accelerates cellular aging through mechanisms including telomere shortening. Evidence-based mind-body interventions like mindfulness meditation, breathwork, and social connection can significantly modulate these pathways, improving metabolic health, immune function, and cellular resilience. A comprehensive approach to longevity medicine must address psychological wellbeing alongside physical interventions.
          </KeyTakeaway>
        </div>
      )
    },
    
    // Slide 16: Integrating the SHIFT Framework - Clinical Implementation
    {
      id: 16,
      title: "Integrating the SHIFT Framework - Clinical Implementation",
      content: (
        <div className="space-y-6">
          <ScientificDiagram 
            title="The SHIFT Framework: Comprehensive Implementation" 
            caption="A systems approach to lifestyle medicine for longevity"
          >
            <svg width="450" height="350" viewBox="0 0 450 350" xmlns="http://www.w3.org/2000/svg">
              {/* Background */}
              <rect x="0" y="0" width="450" height="350" fill="#f8fafc" rx="10" />
              
              {/* Central SHIFT logo */}
              <circle cx="225" cy="175" r="75" fill="#f1f5f9" stroke="#64748b" strokeWidth="2" />
              <text x="225" y="160" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="24">SHIFT</text>
              <text x="225" y="180" textAnchor="middle" fill="#1e293b" fontSize="12">Framework</text>
              <text x="225" y="200" textAnchor="middle" fill="#1e293b" fontSize="10">Sensing, Hormesis, Inflammation,</text>
              <text x="225" y="215" textAnchor="middle" fill="#1e293b" fontSize="10">Function, Transformation</text>
              
              {/* Surrounding elements */}
              <g>
                {/* Assessment */}
                <rect x="50" y="60" width="120" height="60" rx="10" fill="#dbeafe" stroke="#60a5fa" strokeWidth="2" />
                <text x="110" y="85" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="12">Assessment</text>
                <text x="110" y="105" textAnchor="middle" fill="#1e40af" fontSize="10">Biomarkers, Wearables</text>
                
                {/* Personalization */}
                <rect x="280" y="60" width="120" height="60" rx="10" fill="#dcfce7" stroke="#4ade80" strokeWidth="2" />
                <text x="340" y="85" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="12">Personalization</text>
                <text x="340" y="105" textAnchor="middle" fill="#166534" fontSize="10">Genetics, Preferences</text>
                
                {/* Implementation */}
                <rect x="50" y="230" width="120" height="60" rx="10" fill="#e0e7ff" stroke="#818cf8" strokeWidth="2" />
                <text x="110" y="255" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="12">Implementation</text>
                <text x="110" y="275" textAnchor="middle" fill="#3730a3" fontSize="10">Behavior Change, Coaching</text>
                
                {/* Monitoring */}
                <rect x="280" y="230" width="120" height="60" rx="10" fill="#fef3c7" stroke="#fbbf24" strokeWidth="2" />
                <text x="340" y="255" textAnchor="middle" fill="#1e293b" fontWeight="bold" fontSize="12">Monitoring</text>
                <text x="340" y="275" textAnchor="middle" fill="#92400e" fontSize="10">Tracking, Adjustment</text>
              </g>
              
              {/* Connecting arrows */}
              <g>
                <path d="M170,90 L190,90 L190,175 L160,175" stroke="#94a3b8" strokeWidth="2" fill="none" marker-end="url(#arrowhead)" />
                <path d="M280,90 L260,90 L260,175 L290,175" stroke="#94a3b8" strokeWidth="2" fill="none" marker-end="url(#arrowhead)" />
                <path d="M170,260 L190,260 L190,175 L160,175" stroke="#94a3b8" strokeWidth="2" fill="none" marker-end="url(#arrowhead)" />
                <path d="M280,260 L260,260 L260,175 L290,175" stroke="#94a3b8" strokeWidth="2" fill="none" marker-end="url(#arrowhead)" />
              </g>
              
              {/* Arrowhead marker */}
              <defs>
                <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#94a3b8" />
                </marker>
              </defs>
            </svg>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h3 className="text-indigo-800 font-medium mb-2 text-sm">Clinical Implementation</h3>
              <ul className="text-xs text-slate-700 space-y-2">
                <li><span className="font-medium">Assessment:</span> Comprehensive biomarker panel, wearable data, symptom inventory</li>
                <li><span className="font-medium">Prioritization:</span> Target high-leverage interventions based on individual data</li>
                <li><span className="font-medium">Gradual Implementation:</span> Progressive introduction of interventions</li>
                <li><span className="font-medium">Behavior Change:</span> Habit stacking, environmental design, accountability</li>
              </ul>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-blue-800 font-medium mb-2 text-sm">Monitoring & Optimization</h3>
              <ul className="text-xs text-slate-700 space-y-2">
                <li><span className="font-medium">Tracking:</span> Regular biomarker reassessment, wearable data trends</li>
                <li><span className="font-medium">Feedback Loops:</span> Adjust interventions based on response data</li>
                <li><span className="font-medium">Progression:</span> Gradually increase intervention intensity/complexity</li>
                <li><span className="font-medium">Long-term Adherence:</span> Identity-based habits, community support</li>
              </ul>
            </div>
          </div>
          
          <KeyTakeaway>
            The SHIFT framework provides a comprehensive, systems-based approach to lifestyle medicine for longevity. Successful clinical implementation requires personalization, progressive introduction of interventions, effective behavior change strategies, and ongoing monitoring with feedback loops. By addressing the interconnected pathways of Sensing, Hormesis, Inflammation, Function, and Transformation, clinicians can help patients optimize their healthspan and lifespan through evidence-based lifestyle interventions that modulate fundamental cellular pathways of aging.
          </KeyTakeaway>
        </div>
      )
    },
  ];

  return (
    <MentorshipAccessGate>
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-8">
          <div className="flex items-center text-xs text-slate-500 uppercase tracking-wider mb-2">
            <span>Mentorship</span>
            <ChevronRight className="h-3 w-3 mx-1" />
            <span>Lectures</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Lifestyle Interventions for Longevity and Health Optimization: A SHIFT Framework Approach
          </h1>
          <p className="text-slate-600">
            Practical Applications for Clinical Practice
          </p>
        </div>

        <Card className="mb-8 bg-slate-50">
          <div className="p-6">
            <div className="flex items-start space-x-4">
              <div className="bg-indigo-100 rounded-full p-3">
                <FileText className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-1">About This Lecture</h2>
                <p className="text-slate-600 text-sm mb-4">
                  This lecture presents a comprehensive framework for understanding and implementing lifestyle interventions 
                  in clinical practice, focusing on the interconnected pillars of nutrition, exercise, and sleep through the 
                  lens of the SHIFT framework.
                </p>
                <div className="flex flex-wrap gap-2">
                  <div className="bg-slate-200 text-slate-700 px-3 py-1 rounded-full text-xs">
                    30 minutes
                  </div>
                  <div className="bg-slate-200 text-slate-700 px-3 py-1 rounded-full text-xs">
                    Foundational
                  </div>
                  <div className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs">
                    Lifestyle Medicine
                  </div>
                  <div className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs">
                    SHIFT Framework
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Presentation content */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Presentation Slides</h2>
          <Slideshow slides={slides} />
        </div>

        <Footer />
      </div>
    </MentorshipAccessGate>
  );
};

export default LifestyleFoundations3;
